/**
 * @voltxampere/web — Damage audio preferences (Sprint 37-4)
 *
 * AYARLAR > Ses > "Hasar Sesleri" sub-section toggle for the
 * Sprint 37-1 8 damage sounds. When `enabled` flips, wires through
 * to the Sprint 37-2 dispatcher's `setDamageAudioDispatcherEnabled`
 * so sustained loops drain immediately + new events skip dispatch.
 *
 * Pattern mirrors `damage-prefs.ts` (Sprint 36-5) +
 * `explosion-prefs.ts` (Sprint 35-5).
 *
 * Defaults: enabled=true. Hydrate uses `?? true` so v4..v6 settings
 * records (no damageAudio field) start enabled.
 *
 * 6-katman audio guard (per ADR-0059 §"6-layer audio guard"):
 *   1. AYARLAR damageAudio.enabled (this module)
 *   2. AYARLAR audio.muted master mute (Sprint 31)
 *   3. AYARLAR audio.masterVolume gain (Sprint 31)
 *   4. matchMedia('prefers-reduced-motion: reduce')
 *   5. document.hidden (tab arka planda)
 *   6. AudioContext.state === 'suspended' (pre-user-gesture)
 *
 * The dispatcher itself only enforces guard #1 (this enabled flag)
 * + #6 (init not yet performed). Guards 2-5 are enforced by the
 * audio engine + Tone.js master gain wiring (Sprint 31 layer).
 *
 * Engine zero-diff invariant honored — this module imports nothing
 * from `@voltxampere/engine`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect, signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

import { setDamageAudioDispatcherEnabled } from "../audio/damage-audio-dispatcher.js";

/** Master toggle for all 8 damage sound effects. */
export const damageAudioEnabledSignal = signal<boolean>(true);

let initialised = false;
let dispatcherSyncDisposer: (() => void) | null = null;

/**
 * Hydrate the toggle from IDB. Called once during boot. Returns
 * count of fields hydrated (0 or 1) for dev-mode logging.
 *
 * Also installs the reactive sync effect that propagates toggle
 * changes to the dispatcher.
 */
export async function initDamageAudioPrefs(): Promise<number> {
  if (initialised) return 0;
  initialised = true;
  let hydrated = 0;
  try {
    const settings = await getSettings();
    const damageAudio = settings?.damageAudio;
    if (damageAudio && typeof damageAudio.enabled === "boolean") {
      damageAudioEnabledSignal.value = damageAudio.enabled;
      hydrated++;
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA damage-audio-prefs] hydrate failed:", err);
    }
  }
  installDispatcherSync();
  return hydrated;
}

/**
 * Effect that propagates the signal value to the dispatcher
 * whenever the toggle flips. Idempotent install.
 */
function installDispatcherSync(): void {
  if (dispatcherSyncDisposer !== null) {
    dispatcherSyncDisposer();
    dispatcherSyncDisposer = null;
  }
  dispatcherSyncDisposer = effect(() => {
    setDamageAudioDispatcherEnabled(damageAudioEnabledSignal.value);
  });
}

function persist(): void {
  void mergeSettings({
    damageAudio: {
      enabled: damageAudioEnabledSignal.value,
    },
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA damage-audio-prefs] persist failed:", err);
    }
  });
}

/** Toggle damage sound effects (persists immediately). */
export function setDamageAudioEnabled(enabled: boolean): void {
  damageAudioEnabledSignal.value = enabled;
  persist();
}
